import type { ReactNode } from 'react';
import { Alert as HeroAlert } from '@heroui/react/alert';
import { CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * Block-level message — HeroUI's Alert.
 *
 * This is for FORM-LEVEL and page-level messages ("that email or password is
 * incorrect", "a reset link is on its way"). Per-field validation errors are
 * not alerts: they belong to the field, and `Input`'s `error` prop wires them
 * through `FieldError`/`aria-describedby` already.
 *
 * `live` makes the alert an assertive live region so a message that appears
 * after a failed submit is announced without the user having to go find it.
 */
export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const STATUS = {
  info: 'accent',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
} as const;

const ICON = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
} as const;

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Announce the message when it appears. Use on submit failures. */
  live?: boolean;
  className?: string;
}

export function Alert({
  tone = 'info',
  title,
  children,
  live,
  className,
}: AlertProps) {
  const Icon = ICON[tone];

  return (
    <HeroAlert
      status={STATUS[tone]}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={live ? 'assertive' : undefined}
      className={cn('rounded-md text-start', className)}
    >
      <HeroAlert.Indicator>
        <Icon className="size-4" aria-hidden />
      </HeroAlert.Indicator>
      <HeroAlert.Content>
        {title && <HeroAlert.Title>{title}</HeroAlert.Title>}
        {children && <HeroAlert.Description>{children}</HeroAlert.Description>}
      </HeroAlert.Content>
    </HeroAlert>
  );
}
